import NoTaskImage from "../../assets/png/NoTaskImage.png";
import NoSearchTask from "../../assets/png/NoSearchTask.png";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import WbsImport from "@/components/tasks/wbsImport";
import { useUser } from "@/hooks/useUser";
import useProjectWithoutCalulationQuery from "@/api/query/useProjectWithoutCalulationQuery";
import { useEffect, useMemo } from "react";
import { canManageProjectTasksAndContent, normalizeProjectScope } from "@/utils/projectRoleAccess";
import { HelpCircle } from "lucide-react";

type Props = { projectId: string; refetch: () => void; filtered: boolean, setIsOpenPopUp: (data: boolean) => void; };

function NoTask(props: Props) {
  const projects = useProjectWithoutCalulationQuery(false);
  useEffect(() => {
    if (!projects.isFetched) {
      projects.refetch();
    }
  }, [projects.isFetched]);
  const user = useUser()

  const canCreateTask = useMemo(() => {
    if (!user.user || !projects.data?.data.data) return false;

    const project = projects.data.data.data.find(
      (p) => p.projectId == props.projectId
    );
    if (!project) return false;

    const orgRole = user.user.userOrganisation[0]?.role;

    return canManageProjectTasksAndContent(
      user.user,
      orgRole,
      project.assignedUsers,
      normalizeProjectScope(project)
    );
  }, [user.user, projects.data?.data.data, props.projectId]);

  const { t, i18n } = useTranslation();

  const handleDownloadClick = () => {
    const btn = document.getElementById("wbs-download-sample-btn");
    if (btn) {
      btn.click();
    }
  };

  const isFr = i18n.language?.startsWith("fr");

  return (
    <div className="w-full h-full p-4 lg:p-6">
      <div
        className={`w-full px-5 h-full flex overflow-y-auto justify-center items-start border rounded-lg bg-white`}
      >
        <div className="flex flex-col lg:flex-row items-stretch justify-center gap-6 max-w-5xl w-full py-4 lg:py-6">
          {/* Left Card: Actions */}
          <div className="flex flex-col items-center justify-between w-full max-w-md bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
            <div className="w-full flex flex-col items-center text-center">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800">
                {props.filtered ? "Oops!!!" : "Get Started Now!"}
              </h2>
              {props.filtered && (
                <p className="text-sm text-gray-500 mt-1">
                  {t("no_task_with_filter")}
                </p>
              )}
              <div className="p-4 flex items-center justify-center">
                <img
                  src={props.filtered ? NoSearchTask : NoTaskImage}
                  className="w-48 lg:w-56 h-auto object-contain"
                />
              </div>
            </div>

            <div className="w-full flex flex-col items-center gap-3">
              {!props.filtered && (
                <p className="text-sm text-gray-500 text-center font-medium">
                  {canCreateTask ? t("create_your_first_task") : t("ask_your_admin")}
                </p>
              )}

              {canCreateTask && (
                <Button
                  variant={"primary"}
                  onClick={() => props.setIsOpenPopUp(true)}
                  className="w-full tracking-wide py-2 font-semibold text-sm justify-center"
                >
                  {t("create")}
                </Button>
              )}

              {canCreateTask && (
                <div className="w-full flex items-center justify-center gap-3 py-0.5">
                  <div className="h-[1px] bg-gray-200 flex-1"></div>
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
                  <div className="h-[1px] bg-gray-200 flex-1"></div>
                </div>
              )}

              {canCreateTask && (
                <WbsImport
                  projectId={props.projectId}
                  refetch={props.refetch}
                  newTask={() => { }}
                />
              )}
            </div>
          </div>

          {/* Right Card: Setup Guide & Instructions */}
          {!props.filtered && canCreateTask && (
            <div className="w-full max-w-md bg-gray-50 border border-gray-200 rounded-2xl p-5 space-y-4 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2.5 pb-2.5 border-b border-gray-200">
                  <HelpCircle className="h-5 w-5 text-[#0C66E4]" />
                  <h3 className="font-semibold text-gray-800">{t("wbs_guide_title")}</h3>
                </div>

                <div className="space-y-3 mt-3">
                  <div className="p-3 bg-blue-50/70 border border-blue-100 rounded-xl">
                    <h4 className="text-[10px] lg:text-xs font-bold text-blue-800 uppercase tracking-wider mb-0.5">
                      {t("wbs_guide_desc_title")}
                    </h4>
                    <p className="text-[11px] lg:text-xs text-gray-600 leading-relaxed">
                      {t("wbs_guide_desc_text")}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-[10px] lg:text-xs font-bold text-gray-700 uppercase tracking-wider">{t("wbs_rules_title")}</h4>

                    <div className="space-y-2.5 text-[11px] lg:text-xs text-gray-600">
                      <div className="flex gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] shrink-0">1</span>
                        <div>
                          <span className="font-semibold text-gray-800">{t("wbs_rule_1_title")}</span>
                          <p className="text-gray-500 mt-0.5 leading-relaxed">
                            {isFr ? (
                              <>
                                Seuls les fichiers .xlsx sont pris en charge. Veuillez{" "}
                                <button
                                  type="button"
                                  onClick={handleDownloadClick}
                                  className="text-[#0C66E4] hover:text-blue-700 hover:underline font-semibold inline-block focus:outline-none"
                                >
                                  télécharger l'exemple de fichier
                                </button>{" "}
                                pour référence.
                              </>
                            ) : (
                              <>
                                Only .xlsx files are supported. Please{" "}
                                <button
                                  type="button"
                                  onClick={handleDownloadClick}
                                  className="text-[#0C66E4] hover:text-blue-700 hover:underline font-semibold inline-block focus:outline-none"
                                >
                                  download the sample file
                                </button>{" "}
                                for reference.
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] shrink-0">2</span>
                        <div>
                          <span className="font-semibold text-gray-800">{t("wbs_rule_2_title")}</span>
                          <p className="text-gray-500 mt-0.5 leading-relaxed">{t("wbs_rule_2_desc")}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] shrink-0">3</span>
                        <div>
                          <span className="font-semibold text-gray-800">{t("wbs_rule_3_title")}</span>
                          <p className="text-gray-500 mt-0.5 leading-relaxed">{t("wbs_rule_3_desc")}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] shrink-0">4</span>
                        <div>
                          <span className="font-semibold text-gray-800">{t("wbs_rule_4_title")}</span>
                          <p className="text-gray-500 mt-0.5 leading-relaxed">{t("wbs_rule_4_desc")}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold text-[10px] shrink-0">5</span>
                        <div>
                          <span className="font-semibold text-gray-800">{t("wbs_rule_5_title")}</span>
                          <p className="text-gray-500 mt-0.5 leading-relaxed">{t("wbs_rule_5_desc")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NoTask;
